import { useState, useCallback, useEffect } from "react";
import { useTranslations } from "@src/packages/i18n";
import { GrabScreen, GrabContext } from "@src/packages/utils/grab";
import {
  View,
  ScrollView,
  Image,
  ImageSourcePropType,
  Platform,
} from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import { SafeAreaView } from "react-native-safe-area-context";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { NavigationProp } from "@react-navigation/native";
import { useOnboarding } from "../../../onboarding/OnboardingContext";
import {
  CardSurface,
  CloseButton,
  SectionHeader,
  springs,
} from "@louise/moon-ui";
import { EMBER } from "../../../onboarding/theme";
import { useCoverage } from "../../../../../packages/coverage/hooks/useCoverage";
import { useStytch } from "../../../utils/useStytch";
import { logoutWithRecovery } from "../../../utils/logoutWithRecovery";

import type { MainTabParamList, YouStackParamList } from "../../types";
import type { RootStackParamList } from "../../../App";
import type { PanelId } from "./types";
import { YouScreenProvider, useYouScreen } from "./YouScreenContext";
import { getMedicalHistoryProgressSummary } from "@src/packages/medical-history/sectionRegistry";
// Sections
import ProfileHeader from "./sections/ProfileHeader";
import PersonalInfo from "./sections/PersonalInfo";
import MyPassport from "./sections/MyPassport";
import MyJourney from "./sections/MyJourney";
import CoverageBenefits from "./sections/CoverageBenefits";
import ResearchStudies from "./sections/ResearchStudies";
import ConnectionsSharing from "./sections/ConnectionsSharing";
import MedicalFertilityDetail from "./sections/MedicalFertilityDetail";
import type { MedicalFertilityDestination } from "./sections/CareContext";
import InviteFriend from "./sections/InviteFriend";
import ConnectionPrompts from "./sections/ConnectionPrompts";
import Support from "./sections/Support";
import RemindersNotifications from "./sections/RemindersNotifications";
import Integrations from "./sections/Integrations";
import AccountFooter, { LogoutShortcut } from "./sections/AccountFooter";
import Preferences from "./sections/Preferences";

// Shared components
import { GroupedCard, YouIllustration } from "./components";
import { YouOverviewRow } from "./components/YouOverviewRow";

// Detail dialog
import DetailDialog from "./components/DetailDialog";

// Modals
import PartnerInviteModal from "./modals/PartnerInviteModal";
import DataSharingModal from "./modals/DataSharingModal";
import LogoutModal from "./modals/LogoutModal";
import DeleteAccountModal from "./modals/DeleteAccountModal";
import RoadmapModal, {
  resolveJourneyPath,
  resolveRoadmapPhaseId,
} from "./modals/RoadmapModal";
import { useJourneySignals } from "@src/packages/cycles/hooks/useJourneySignals";
import { selectJourneyCycle } from "@src/packages/cycles/presenters/journeyPhase";
import PastCyclesModal from "./modals/PastCyclesModal";
import IntentPickerModal, {
  type IntentPickerStage,
  type IntentPickerPrimaryIntent,
} from "./modals/IntentPickerModal";
import { useSaveIntentChoice } from "./modals/useSaveIntentChoice";
import Setup2FAModal from "./modals/Setup2FAModal";
import Disable2FAModal from "./modals/Disable2FAModal";
import EndCycleModal from "./modals/EndCycleModal";
import { useActiveCycle, useCycles } from "@src/packages/cycles";
import { useAppleHealthImportStatus } from "@src/packages/health";
import {
  countSupportedNotificationPreferences,
  useNotificationDeliverability,
} from "@src/packages/notifications";
import { useCareConnections } from "@src/packages/patients/hooks/useCareConnections";
import { useCarePersons } from "@src/packages/patients/hooks/useCarePersons";

// Detail routes (kept for direct navigation)
import NotificationsSettingsScreen from "./screens/NotificationsSettingsScreen";
import IntegrationsSettingsScreen from "./screens/IntegrationsSettingsScreen";
import AccountSettingsScreen from "./screens/AccountSettingsScreen";
import PersonalInfoDetailScreen from "./screens/PersonalInfoDetailScreen";
import MedicalFertilityDetailScreen from "./screens/MedicalFertilityDetailScreen";
import CoverageBenefitsDetailScreen from "./screens/CoverageBenefitsDetailScreen";
import ConnectionsDetailScreen from "./screens/ConnectionsDetailScreen";
import ResearchStudiesDetailScreen from "./screens/ResearchStudiesDetailScreen";
import PreferencesSettingsScreen from "./screens/PreferencesSettingsScreen";
import YouScreenSkeleton from "./YouScreenSkeleton";

// 3D icons
import { ROW_ICONS } from "./icons";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";

const PATIENT_SKELETON_GRACE_MS = 2_000;

/**
 * The profile can render safely from its local defaults while patient data is
 * still arriving. Keep the skeleton for a normal, quick request, but never
 * let an identity or network request leave the entire screen unusable.
 */
function usePatientSkeletonVisibility(patientLoading: boolean) {
  const [loadingExceededGrace, setLoadingExceededGrace] = useState(false);

  useEffect(() => {
    if (!patientLoading) {
      setLoadingExceededGrace(false);
      return;
    }

    const timeout = setTimeout(
      () => setLoadingExceededGrace(true),
      PATIENT_SKELETON_GRACE_MS,
    );
    return () => clearTimeout(timeout);
  }, [patientLoading]);

  return patientLoading && !loadingExceededGrace;
}

// ── Panel config ──

const PANEL_TITLE_KEYS: Record<Exclude<PanelId, null>, string> = {
  "personal-info": "you.panel_titles.personal_info",
  "medical-fertility": "you.panel_titles.medical_fertility",
  "coverage-benefits": "you.panel_titles.coverage_benefits",
  connections: "you.panel_titles.connections",
  "research-studies": "you.panel_titles.research_studies",
  notifications: "you.panel_titles.notifications",
  integrations: "you.panel_titles.integrations",
  preferences: "you.panel_titles.preferences",
  account: "you.panel_titles.account",
};

const PANEL_ICONS: Record<Exclude<PanelId, null>, ImageSourcePropType> = {
  "personal-info": ROW_ICONS.personalInfo,
  "medical-fertility": ROW_ICONS.medicalFertility,
  "coverage-benefits": ROW_ICONS.coverageBenefits,
  connections: ROW_ICONS.connections,
  "research-studies": ROW_ICONS.research,
  notifications: ROW_ICONS.notifications,
  integrations: ROW_ICONS.integrations,
  preferences: ROW_ICONS.preferences,
  account: ROW_ICONS.account,
};

type YouOverviewProps = NativeStackScreenProps<
  YouStackParamList,
  "YouOverview"
>;

function YouOverviewScreen({ navigation, route }: YouOverviewProps) {
  const t = useTranslations();
  const { data } = useOnboarding();
  const {
    state,
    dispatch,
    badgeDataStatus,
    retryBadgeData,
    canRetryBadgeData,
    isRetryingBadgeData,
    patient,
    personalMedical,
    patientLoading,
    preferences,
    preferencesStatus,
    notificationPreferences,
    notificationPreferencesStatus,
    researchConsent,
    partnerInvite,
  } = useYouScreen();
  const showPatientSkeleton = usePatientSkeletonVisibility(patientLoading);
  const {
    state: coverageState,
    policies,
    primaryPolicy,
    hasInsurance,
    verificationStatus,
    fertilityCoverage,
    isLoading: coverageLoading,
    isError: coverageError,
    isFetching: coverageFetching,
    refetch: refetchCoverage,
  } = useCoverage();
  const stytch = useStytch();
  const { revokeRegisteredDevice } = useNotificationDeliverability();
  const { data: cycles } = useCycles();
  const rootNavigation =
    navigation.getParent<NavigationProp<RootStackParamList>>()!;

  // Derive current goal from API (prefer fertilityIntent.goal) or onboarding context
  const currentIntent =
    patient?.fertilityIntent?.goal ??
    patient?.treatments?.[0]?.intent ??
    data.treatmentIntent ??
    null;

  // Only while the map is open — see `useJourneySignals`.
  const journeyMapOpen = state.activeModal === "roadmap";
  const journeySignals = useJourneySignals({
    cycle: selectJourneyCycle(cycles),
    cycles,
    // The patient record only, deliberately. `currentIntent` also falls back to
    // the onboarding context, which Home has no access to — reading it here
    // meant the same dialog ticked "set your journey goal" on the profile and
    // left it blank on Home.
    hasGoal: !!(
      patient?.fertilityIntent?.goal ?? patient?.treatments?.[0]?.intent
    ),
    enabled: journeyMapOpen,
  });

  // Scale + fade entrance animation
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);

  // Animate in on mount (useState initializer runs once)
  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const goBack = useCallback(() => {
    if (rootNavigation.canGoBack()) {
      rootNavigation.goBack();
    } else {
      (rootNavigation as { navigate: (screen: string) => void }).navigate(
        "Home",
      );
    }
  }, [rootNavigation]);

  const handleClose = useCallback(() => {
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(goBack)();
    });
  }, [scale, opacity, goBack]);

  const intentChoice = useSaveIntentChoice();
  const saveIntentChoice = intentChoice.save;
  const handleSelectIntent = useCallback(
    async (
      goal: string,
      stage: IntentPickerStage | undefined,
      primaryIntent?: IntentPickerPrimaryIntent,
    ) => {
      const saved = await saveIntentChoice(goal, stage, primaryIntent);
      if (saved) dispatch({ type: "SHOW_MODAL", modal: null });
      return saved;
    },
    [dispatch, saveIntentChoice],
  );

  const activeNotifCount = countSupportedNotificationPreferences(
    notificationPreferences,
  );
  const notificationSummary = notificationPreferencesStatus.isLoading
    ? t("you.overview.notifications_loading")
    : notificationPreferencesStatus.hasLoadError || activeNotifCount === null
      ? t("you.overview.notifications_unavailable")
      : t("you.overview.notifications_active", {
          params: { count: String(activeNotifCount) },
        });

  const { data: appleHealthImport } = useAppleHealthImportStatus();

  const { partnerConnected } = useCarePersons();
  const { data: activeCycleForCount } = useActiveCycle();
  const clinicConnected = !!activeCycleForCount?.providerId;
  const connectedCount = (partnerConnected ? 1 : 0) + (clinicConnected ? 1 : 0);
  const { data: careConnections = [] } = useCareConnections();
  const recordedCareConnectionCount = careConnections.length;

  const filledPersonalFields = patientLoading
    ? 0
    : [
        personalMedical.firstName,
        personalMedical.lastName,
        personalMedical.email,
        personalMedical.phone,
        personalMedical.dateOfBirth,
        personalMedical.gender,
        personalMedical.address,
      ].filter(Boolean).length;
  const medicalHistoryProgress = patient
    ? getMedicalHistoryProgressSummary(patient)
    : null;

  const openSection = useCallback(
    (panelId: Exclude<PanelId, null>) => {
      dispatch({ type: "SET_ACTIVE_PANEL", panel: panelId });
    },
    [dispatch],
  );

  const closePanel = useCallback(() => {
    dispatch({ type: "CLOSE_PANEL" });
  }, [dispatch]);

  const navigateMedicalCare = useCallback(
    (destination: MedicalFertilityDestination) => {
      // The detail dialog is useful for short edits, but these are full care
      // workflows. Close it before handing off to the single source of truth.
      closePanel();
      if (destination.screen === "Documents") {
        rootNavigation.navigate("Home", {
          screen: "Care",
          params: {
            screen: "Documents",
            params: destination.params,
          },
        });
        return;
      }
      rootNavigation.navigate("Home", {
        screen: "Care",
        params: { screen: destination.screen },
      });
    },
    [closePanel, rootNavigation],
  );

  // The overview dialog and native detail routes intentionally share these
  // section components. DetailDialog owns the compact modal frame, while
  // YouDetailScreenLayout owns the full-screen route frame.
  // ── Detail content for dialog ──

  function getDetailContent(): React.ReactNode | null {
    switch (state.activePanel) {
      case "personal-info":
        return <PersonalInfo data={personalMedical} onClose={closePanel} />;
      case "medical-fertility":
        return (
          <MedicalFertilityDetail
            personalMedical={personalMedical}
            onNavigate={navigateMedicalCare}
          />
        );
      case "coverage-benefits":
        return (
          <CoverageBenefits
            policies={policies}
            primaryPolicy={primaryPolicy}
            hasInsurance={hasInsurance}
            coverageState={coverageState}
            verificationStatus={verificationStatus}
            fertilityCoverage={fertilityCoverage}
            isLoading={coverageLoading}
            isError={coverageError}
            onRetry={refetchCoverage}
            isRetrying={coverageFetching}
          />
        );
      case "connections":
        return (
          <ConnectionsSharing
            connections={state.connections}
            dispatch={dispatch}
          />
        );
      case "research-studies":
        return (
          <ResearchStudies
            studies={state.studies}
            anonymousDataContribution={researchConsent.value}
            anonymousDataContributionLoadFailed={researchConsent.hasLoadError}
            anonymousDataContributionSaveFailed={researchConsent.hasSaveError}
            onRetryAnonymousDataContributionLoad={researchConsent.retryLoad}
            isRetryingAnonymousDataContributionLoad={
              researchConsent.isRetryingLoad
            }
            onAnonymousDataContributionChange={(value) =>
              dispatch({ type: "SET_ANONYMOUS_DATA", value })
            }
          />
        );
      case "notifications":
        return (
          <RemindersNotifications
            notificationPreferences={notificationPreferences}
            notificationPreferencesStatus={notificationPreferencesStatus}
            dispatch={dispatch}
          />
        );
      case "integrations":
        return <Integrations />;
      case "preferences":
        return (
          <Preferences
            preferences={preferences}
            status={preferencesStatus}
            dispatch={dispatch}
          />
        );
      case "account":
        return <AccountFooter dispatch={dispatch} />;
      default:
        return null;
    }
  }

  const detailContent = getDetailContent();
  const detailTitle = state.activePanel
    ? t(PANEL_TITLE_KEYS[state.activePanel])
    : "";
  const detailIcon = state.activePanel
    ? PANEL_ICONS[state.activePanel]
    : undefined;
  // Modal actions replace the detail panel. Keeping both native/web modals
  // mounted leaves the panel above the action dialog in some environments.
  const showDetailDialog = detailContent !== null && state.activeModal === null;

  return (
    <GrabScreen>
      <GrabContext value={{ screen: "you" }}>
        <View style={{ flex: 1, overflow: "hidden" }}>
          {/* Background image — stays static, never scales */}
          <Image
            source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
            style={{
              position: "absolute",
              width: "115%",
              height: "115%",
              left: "-7.5%",
              top: "-7.5%",
            }}
            resizeMode="cover"
          />

          <Animated.View style={animatedStyle}>
            <SafeAreaView style={{ flex: 1 }}>
              <View style={{ flex: 1 }}>
                <ScrollView
                  contentContainerStyle={{
                    paddingHorizontal: 16,
                    paddingBottom: 100,
                    maxWidth: 520,
                    width: "100%",
                    alignSelf: "center",
                  }}
                  showsVerticalScrollIndicator={false}
                >
                  {/* Avatar centered */}
                  <View
                    style={{
                      alignItems: "center",
                      paddingTop: SCREEN_PADDING_TOP,
                      zIndex: 1,
                    }}
                  >
                    <ProfileHeader data={data} avatarOnly />
                  </View>

                  {/* Close button — above the card so it's not blocked by avatar z-index */}
                  <View
                    style={{
                      alignSelf: "flex-end",
                      marginRight: 16,
                      marginBottom: -32,
                      zIndex: 3,
                    }}
                  >
                    <CloseButton onPress={handleClose} />
                  </View>

                  {/* Frosted glass card */}
                  <CardSurface
                    style={{
                      paddingHorizontal: 20,
                      paddingBottom: 20,
                      paddingTop: 52,
                      marginTop: -40,
                    }}
                  >
                    <ProfileHeader data={data} infoOnly />

                    {showPatientSkeleton ? (
                      <YouScreenSkeleton />
                    ) : (
                      <>
                        {/* Inline sections */}
                        <MyJourney />
                        <ConnectionPrompts />
                        <MyPassport
                          badges={state.badges}
                          badgeDataStatus={badgeDataStatus}
                          onRetryBadgeData={
                            canRetryBadgeData ? retryBadgeData : undefined
                          }
                          isRetryingBadgeData={isRetryingBadgeData}
                          initialBadgeId={route.params?.badgeId}
                          initialBadgeIds={route.params?.badgeIds}
                        />

                        {/* Collapsed info rows */}
                        <SectionHeader>
                          {t("you.overview.your_info_header")}
                        </SectionHeader>
                        <GroupedCard>
                          <YouOverviewRow
                            icon={
                              <YouIllustration
                                source={ROW_ICONS.personalInfo}
                              />
                            }
                            label={t("you.panel_titles.personal_info")}
                            value={t("you.overview.personal_info_value", {
                              params: {
                                filled: String(filledPersonalFields),
                                total: "7",
                              },
                            })}
                            onPress={() => openSection("personal-info")}
                          />
                          <YouOverviewRow
                            icon={
                              <YouIllustration
                                source={ROW_ICONS.medicalFertility}
                              />
                            }
                            label={t("you.panel_titles.medical_fertility")}
                            value={
                              medicalHistoryProgress
                                ? t("you.overview.medical_fertility_progress", {
                                    params: {
                                      completed: String(
                                        medicalHistoryProgress.completedSections,
                                      ),
                                      total: String(
                                        medicalHistoryProgress.totalSections,
                                      ),
                                    },
                                  })
                                : t("you.overview.medical_fertility_not_set")
                            }
                            divider
                            onPress={() => openSection("medical-fertility")}
                          />
                          <YouOverviewRow
                            icon={
                              <YouIllustration
                                source={ROW_ICONS.coverageBenefits}
                              />
                            }
                            label={t("you.panel_titles.coverage_benefits")}
                            value={
                              hasInsurance
                                ? (primaryPolicy?.payer?.displayName ??
                                  primaryPolicy?.payer?.name ??
                                  primaryPolicy?.insuranceCarrier ??
                                  t("you.overview.coverage_on_file"))
                                : t("you.overview.coverage_add")
                            }
                            valueColor={
                              hasInsurance ? undefined : EMBER.colors.textLight
                            }
                            divider
                            onPress={() => openSection("coverage-benefits")}
                          />
                          <YouOverviewRow
                            icon={
                              <YouIllustration source={ROW_ICONS.connections} />
                            }
                            label={t("you.panel_titles.connections")}
                            value={
                              recordedCareConnectionCount > 0
                                ? t("you.overview.connections_recorded_count", {
                                    params: {
                                      count: String(
                                        recordedCareConnectionCount,
                                      ),
                                    },
                                  })
                                : connectedCount > 0
                                  ? t("you.overview.connections_count", {
                                      params: { count: String(connectedCount) },
                                    })
                                  : t("you.overview.connections_none")
                            }
                            divider
                            onPress={() => openSection("connections")}
                          />
                          <YouOverviewRow
                            icon={
                              <YouIllustration source={ROW_ICONS.research} />
                            }
                            label={t("you.panel_titles.research_studies")}
                            value={
                              state.studies.length > 0
                                ? t("you.overview.research_matched", {
                                    params: {
                                      count: String(state.studies.length),
                                    },
                                  })
                                : t("you.overview.research_none")
                            }
                            divider
                            onPress={() => openSection("research-studies")}
                          />
                        </GroupedCard>

                        {/* Settings rows */}
                        <SectionHeader>
                          {t("you.overview.settings_header")}
                        </SectionHeader>
                        <GroupedCard>
                          {/* The global notification feed lives in HeaderActions; this row only opens notification settings. */}
                          <YouOverviewRow
                            icon={
                              <YouIllustration
                                source={ROW_ICONS.notifications}
                              />
                            }
                            label={t("you.panel_titles.notifications")}
                            value={notificationSummary}
                            onPress={() => openSection("notifications")}
                          />
                          <YouOverviewRow
                            icon={
                              <YouIllustration
                                source={ROW_ICONS.integrations}
                              />
                            }
                            label={t("you.panel_titles.integrations")}
                            value={
                              appleHealthImport
                                ? t(
                                    "you.overview.integrations_apple_health_data",
                                  )
                                : t("you.overview.integrations_none")
                            }
                            divider
                            onPress={() => openSection("integrations")}
                          />
                          <YouOverviewRow
                            icon={
                              <YouIllustration source={ROW_ICONS.preferences} />
                            }
                            label={t("you.panel_titles.preferences")}
                            value={t(
                              `you.fertility_profile.language_labels.${
                                preferences.language
                              }`,
                            )}
                            divider
                            onPress={() => openSection("preferences")}
                          />
                          <YouOverviewRow
                            icon={
                              <YouIllustration source={ROW_ICONS.account} />
                            }
                            label={t("you.panel_titles.account")}
                            divider
                            onPress={() => openSection("account")}
                          />
                        </GroupedCard>

                        <InviteFriend />
                        <Support />
                        <LogoutShortcut
                          onPress={() =>
                            dispatch({ type: "SHOW_MODAL", modal: "logout" })
                          }
                        />
                      </>
                    )}
                  </CardSurface>
                </ScrollView>
              </View>
            </SafeAreaView>

            <DetailDialog
              visible={showDetailDialog}
              title={detailTitle}
              icon={detailIcon}
              onClose={closePanel}
            >
              {detailContent}
            </DetailDialog>

            {/* Modals */}
            <PartnerInviteModal
              visible={state.activeModal === "partner-invite"}
              name={state.partnerInviteName}
              phone={state.partnerInvitePhone}
              onChangeName={(name) =>
                dispatch({ type: "SET_PARTNER_INVITE_NAME", name })
              }
              onChangePhone={(phone) =>
                dispatch({ type: "SET_PARTNER_INVITE_PHONE", phone })
              }
              onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
              onSend={(name, phone, sharingCategories) =>
                dispatch({
                  type: "SEND_PARTNER_INVITE",
                  name,
                  phone,
                  sharingCategories,
                })
              }
              isSending={partnerInvite.isSending}
              sent={partnerInvite.isSuccess}
              isEditing={partnerInvite.isEditing}
              initialSharingCategories={partnerInvite.initialSharingCategories}
              scopeReady={partnerInvite.scopeReady}
              error={partnerInvite.error}
              onDismissError={partnerInvite.resetError}
            />

            <DataSharingModal
              visible={state.dataSharingModal !== null}
              connectionType={state.dataSharingModal}
              connections={state.connections}
              dispatch={dispatch}
              onClose={() =>
                dispatch({
                  type: "SHOW_DATA_SHARING_MODAL",
                  connectionType: null,
                })
              }
            />

            <LogoutModal
              visible={state.activeModal === "logout"}
              onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
              onConfirm={async () => {
                const sessionRevoked = await logoutWithRecovery({
                  revokeRegisteredDevice,
                  revokeSession: () =>
                    stytch.session.revoke({ forceClear: true }),
                });
                dispatch({ type: "SHOW_MODAL", modal: null });
                if (
                  !sessionRevoked &&
                  Platform.OS === "web" &&
                  typeof window !== "undefined"
                ) {
                  window.location.reload();
                }
              }}
            />

            <DeleteAccountModal
              visible={
                state.activePanel === "account" &&
                state.activeModal === "delete"
              }
              confirmText={state.deleteConfirmText}
              onChangeText={(text) =>
                dispatch({ type: "SET_DELETE_CONFIRM_TEXT", text })
              }
              onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
            />

            <Setup2FAModal
              visible={
                state.activePanel === "account" &&
                state.activeModal === "setup-2fa"
              }
              onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
              onComplete={() => dispatch({ type: "SHOW_MODAL", modal: null })}
            />

            <Disable2FAModal
              visible={
                state.activePanel === "account" &&
                state.activeModal === "disable-2fa"
              }
              onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
              onConfirm={() => dispatch({ type: "SHOW_MODAL", modal: null })}
            />

            <RoadmapModal
              visible={state.activeModal === "roadmap"}
              currentPhaseId={resolveRoadmapPhaseId(
                patient?.fertilityIntent?.stage,
                currentIntent,
                cycles,
              )}
              journeyPath={resolveJourneyPath(cycles, currentIntent)}
              signals={journeySignals}
              onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
            />

            <PastCyclesModal
              visible={state.activeModal === "past-cycles"}
              onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
            />

            <IntentPickerModal
              visible={state.activeModal === "intent-picker"}
              currentIntent={currentIntent}
              genderOverride={state.intentPickerGenderOverride}
              onSelect={handleSelectIntent}
              onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
              isSaving={intentChoice.isSaving}
              saveError={
                intentChoice.hasError
                  ? t("you.intent_picker.journey_save_failed")
                  : null
              }
            />

            <EndCycleModal
              visible={state.activeModal === "end-cycle"}
              onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
              preservationCycleType={activeCycleForCount?.cycleType}
              onOpenArticle={(articleId: string) => {
                const rootNav =
                  navigation.getParent<NavigationProp<MainTabParamList>>();
                rootNav?.navigate("Learn", {
                  screen: "ArticleDetail",
                  params: { articleId, returnTab: "Care" },
                });
              }}
            />
          </Animated.View>
        </View>
      </GrabContext>
    </GrabScreen>
  );
}

const Stack = createNativeStackNavigator<YouStackParamList>();

function YouNavigatorContent() {
  return (
    <>
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        <Stack.Screen name="YouOverview" component={YouOverviewScreen} />
        <Stack.Screen
          name="NotificationsSettings"
          component={NotificationsSettingsScreen}
        />
        <Stack.Screen
          name="IntegrationsSettings"
          component={IntegrationsSettingsScreen}
        />
        <Stack.Screen
          name="AccountSettings"
          component={AccountSettingsScreen}
        />
        <Stack.Screen
          name="PersonalInfoDetail"
          component={PersonalInfoDetailScreen}
        />
        <Stack.Screen
          name="MedicalFertilityDetail"
          component={MedicalFertilityDetailScreen}
        />
        <Stack.Screen
          name="CoverageBenefitsDetail"
          component={CoverageBenefitsDetailScreen}
        />
        <Stack.Screen
          name="ConnectionsDetail"
          component={ConnectionsDetailScreen}
        />
        <Stack.Screen
          name="ResearchStudiesDetail"
          component={ResearchStudiesDetailScreen}
        />
        <Stack.Screen
          name="PreferencesSettings"
          component={PreferencesSettingsScreen}
        />
      </Stack.Navigator>
    </>
  );
}

export default function YouNavigator() {
  return (
    <YouScreenProvider>
      <YouNavigatorContent />
    </YouScreenProvider>
  );
}
